/**
 * Web Component entry point for the Matrix widget.
 *
 * Importing this module registers the <legion-matrix> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/matrix-web-component';
 *
 * // Then use in HTML:
 * // <legion-matrix base-url="https://your-domain.com" theme="light" />
 * ```
 */

import { attachAuthBridge, type AuthBridgeHandle, AUTH_BRIDGE_OBSERVED_ATTRS } from "../shared/auth-bridge";

const MATRIX_CSS_ATTRIBUTES = [
  "matrix-background-color",
  "matrix-text-color",
  "matrix-secondary-text-color",
  "matrix-border-color",
  "matrix-border-radius",
  "matrix-padding",
  "matrix-shadow",
  "matrix-primary-color",
  "matrix-accent-color",
  "matrix-card-bg",
  "matrix-card-hover-bg",
  "matrix-card-border",
  "matrix-card-radius",
  "matrix-card-shadow",
  "matrix-card-padding",
  "matrix-card-gap",
  "matrix-icon-bg",
  "matrix-icon-color",
  "matrix-icon-size",
  "matrix-icon-radius",
  "matrix-indicator-color",
  "matrix-indicator-size",
  "matrix-badge-bg",
  "matrix-badge-color",
  "matrix-font-family",
  "matrix-title-size",
  "matrix-title-weight",
  "matrix-body-size",
  "matrix-body-weight",
  "matrix-timestamp-size",
  "matrix-timestamp-color",
  "matrix-empty-icon-color",
  "matrix-empty-icon-size",
  "matrix-empty-title-size",
  "matrix-empty-text-color",
  "matrix-button-bg",
  "matrix-button-color",
  "matrix-button-radius",
  "matrix-button-padding",
  "matrix-transition-duration",
  "matrix-hover-scale",
  "matrix-animation-timing",
  "matrix-min-height",
  "matrix-max-width",
  "matrix-gap",
] as const;

class LegionMatrixElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "base-url",
      "theme",
      "width",
      "height",
      "empty-title",
      "empty-subtitle",
      "accent-color",
      "empty-icon",
      ...MATRIX_CSS_ATTRIBUTES,
    
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get baseUrl(): string {
    return this.getAttribute("base-url") || window.location.origin;
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "100%";
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "400";
  }

  private get emptyTitle(): string {
    return this.getAttribute("empty-title") || "You're all caught up";
  }

  private get emptySubtitle(): string {
    return this.getAttribute("empty-subtitle") || "No updates right now";
  }

  private get accentColor(): string {
    return (
      this.getAttribute("accent-color") ||
      (this.theme === "dark" ? "gray" : "blue")
    );
  }

  private get emptyIcon(): string {
    const value = (
      this.getAttribute("empty-icon") || "bell"
    ).toLowerCase();
    return ["bell", "gift", "activity", "trophy"].includes(value)
      ? value
      : "bell";
  }

  render(): void {
    if (!this.shadowRoot) return;

    const url = new URL(`${this.baseUrl}/widget/matrix`);
    url.searchParams.set("theme", this.theme);
    url.searchParams.set("emptyTitle", this.emptyTitle);
    url.searchParams.set("emptySubtitle", this.emptySubtitle);
    url.searchParams.set("accentColor", this.accentColor);
    url.searchParams.set("emptyIcon", this.emptyIcon);

    MATRIX_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    const iframe = document.createElement("iframe");
    iframe.src = url.toString();
    iframe.style.width = this.widgetWidth.includes("%")
      ? this.widgetWidth
      : `${this.widgetWidth}px`;
    iframe.style.height = this.widgetHeight.includes("%")
      ? this.widgetHeight
      : `${this.widgetHeight}px`;
    iframe.frameBorder = "0";
    iframe.scrolling = "auto";
    iframe.title = "Matrix Widget";

    // Shadow DOM styling
    const bgColor =
      this.getAttribute("matrix-background-color") ||
      (this.theme === "dark" ? "#1a1a1a" : "white");
    const borderRadius =
      this.getAttribute("matrix-border-radius") || "8px";
    const shadow =
      this.getAttribute("matrix-shadow") ||
      "0 2px 10px rgba(0, 0, 0, 0.1)";

    const style = document.createElement("style");
    style.textContent = `
      :host { display: block; width: 100%; min-height: ${this.widgetHeight}px; }
      iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: ${borderRadius};
        box-shadow: ${shadow};
        background: ${bgColor};
      }
    `;

    iframe.onload = () => {
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: { widget: "matrix", theme: this.theme },
        })
      );
    };

    iframe.onerror = () => {
      if (this.shadowRoot) {
        this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load widget</div>`;
      }
    };

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.baseUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-matrix")
) {
  customElements.define("legion-matrix", LegionMatrixElement);
}

export { LegionMatrixElement };
